import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Users, UserPlus, Trash2, Loader2, Copy, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import {
  partnerApi, formatPartnerError, canPartnerAdmin, isPartnerOwner,
  PARTNER_ROLE_LABELS,
} from "./partnerApi";
import "./PartnerPortal.css";

const ROLES = ["owner", "admin", "developer"];

export default function PartnerUsers() {
  const { me } = useOutletContext();
  const canWrite = canPartnerAdmin(me);
  const isOwner = isPartnerOwner(me);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", partner_role: "developer" });
  const [busy, setBusy] = useState(false);
  const [freshInvite, setFreshInvite] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await partnerApi.get("/partner/portal/users");
      setUsers(data.users || []);
    } catch (e) { toast.error(formatPartnerError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    setBusy(true);
    try {
      const { data } = await partnerApi.post("/partner/portal/users/invite", {
        email: form.email.trim(),
        name: form.name.trim() || undefined,
        partner_role: form.partner_role,
      });
      setFreshInvite(data);
      setForm({ email: "", name: "", partner_role: "developer" });
      setShowForm(false);
      load();
      toast.success("Invitation sent");
    } catch (e) { toast.error(formatPartnerError(e)); }
    finally { setBusy(false); }
  };

  const updateRole = async (id, partner_role) => {
    try {
      await partnerApi.patch(`/partner/portal/users/${id}`, { partner_role });
      toast.success("Role updated");
      load();
    } catch (e) { toast.error(formatPartnerError(e)); }
  };

  const setStatus = async (id, status) => {
    try {
      await partnerApi.patch(`/partner/portal/users/${id}`, { status });
      toast.success(`Status set to ${status}`);
      load();
    } catch (e) { toast.error(formatPartnerError(e)); }
  };

  const remove = async (id, email) => {
    if (!window.confirm(`Remove ${email}? They lose portal access immediately.`)) return;
    try {
      await partnerApi.delete(`/partner/portal/users/${id}`);
      toast.success("User removed");
      load();
    } catch (e) { toast.error(formatPartnerError(e)); }
  };

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };

  return (
    <div data-testid="pp-users">
      <h1 className="hw-pp__page-title">
        <Users className="w-5 h-5"
                style={{ display: "inline", verticalAlign: "text-bottom", marginRight: "0.4rem" }} />
        Team members
      </h1>
      <p className="hw-pp__page-sub">
        Invite teammates to your Partner Portal. Owners can promote to any role;
        Admins can invite Admins + Developers.
      </p>

      {freshInvite && (
        <div className="hw-pp__panel" data-testid="pp-fresh-invite"
              style={{ borderColor: "#5EEAD4" }}>
          <h3>
            <span>Invitation sent to <strong>{freshInvite.email}</strong></span>
            <button className="hw-pp__btn" onClick={() => setFreshInvite(null)}>Dismiss</button>
          </h3>
          <div className="hw-pp__reveal-hint">
            An email was sent. As a backup, copy the invite link and share it directly:
          </div>
          <div className="hw-pp__reveal" data-testid="pp-fresh-invite-link">
            {freshInvite.invite_link}
          </div>
          <button className="hw-pp__btn" onClick={() => copy(freshInvite.invite_link)}
                   data-testid="pp-fresh-invite-copy">
            <Copy className="w-3.5 h-3.5" /> Copy invite link
          </button>
        </div>
      )}

      <div className="hw-pp__panel">
        <h3>
          <span>{users.length} member{users.length === 1 ? "" : "s"}</span>
          <div style={{ display: "flex", gap: "0.35rem" }}>
            <button className="hw-pp__btn" onClick={load}>
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            {canWrite && (
              <button className="hw-pp__btn hw-pp__btn--primary"
                       onClick={() => setShowForm(!showForm)}
                       data-testid="pp-invite-btn">
                <UserPlus className="w-3.5 h-3.5" /> Invite
              </button>
            )}
          </div>
        </h3>

        {showForm && (
          <div style={{ padding: "0.7rem", background: "rgba(15,23,42,0.5)",
                          borderRadius: "0.5rem", marginBottom: "0.7rem",
                          display: "grid", gridTemplateColumns: "1.4fr 1fr 0.8fr auto", gap: "0.5rem" }}>
            <label className="hw-pp__field" style={{ margin: 0 }}>
              <span className="hw-pp__field-label">Email</span>
              <input className="hw-pp__input" type="email" value={form.email}
                      onChange={(e) => setForm({...form, email: e.target.value})}
                      data-testid="pp-invite-email" />
            </label>
            <label className="hw-pp__field" style={{ margin: 0 }}>
              <span className="hw-pp__field-label">Name (optional)</span>
              <input className="hw-pp__input" value={form.name}
                      onChange={(e) => setForm({...form, name: e.target.value})}
                      data-testid="pp-invite-name" />
            </label>
            <label className="hw-pp__field" style={{ margin: 0 }}>
              <span className="hw-pp__field-label">Role</span>
              <select className="hw-pp__input" value={form.partner_role}
                       onChange={(e) => setForm({...form, partner_role: e.target.value})}
                       data-testid="pp-invite-role">
                {ROLES.filter((r) => isOwner || r !== "owner").map((r) => (
                  <option key={r} value={r}>{PARTNER_ROLE_LABELS[r]}</option>
                ))}
              </select>
            </label>
            <div style={{ display: "flex", alignItems: "end", gap: "0.35rem" }}>
              <button className="hw-pp__btn" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="hw-pp__btn hw-pp__btn--primary" onClick={invite}
                       disabled={busy || !form.email.includes("@")}
                       data-testid="pp-invite-submit">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
                Send invite
              </button>
            </div>
          </div>
        )}

        {loading && !users.length ? (
          <div style={{ color: "#5EEAD4" }}>
            <Loader2 className="w-4 h-4 animate-spin" style={{ display: "inline" }} /> loading…
          </div>
        ) : (
          <table className="hw-pp__table" data-testid="pp-users-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last login</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`pp-user-row-${u.id}`}>
                  <td>
                    <strong style={{ color: "#F1F5F9" }}>{u.email}</strong>
                    {u.id === me.partner_user.id && (
                      <span style={{ marginLeft: "0.35rem", color: "#5EEAD4", fontSize: "0.7rem" }}>
                        (you)
                      </span>
                    )}
                  </td>
                  <td>{u.name || "—"}</td>
                  <td>
                    {isOwner && u.id !== me.partner_user.id ? (
                      <select className="hw-pp__input"
                               style={{ padding: "0.2rem 0.4rem", width: "auto", fontSize: "0.8rem" }}
                               value={u.partner_role}
                               onChange={(e) => updateRole(u.id, e.target.value)}
                               data-testid={`pp-user-role-${u.id}`}>
                        {ROLES.map((r) => (
                          <option key={r} value={r}>{PARTNER_ROLE_LABELS[r]}</option>
                        ))}
                      </select>
                    ) : (
                      <span>{PARTNER_ROLE_LABELS[u.partner_role] || u.partner_role}</span>
                    )}
                  </td>
                  <td>
                    <span className={`hw-pp__pill hw-pp__pill--${u.status || "invited"}`}>
                      {u.status || "invited"}
                    </span>
                  </td>
                  <td style={{ fontSize: "0.75rem", color: "#94A3B8" }}>
                    {u.last_login_at ? u.last_login_at.slice(0, 10) : "—"}
                  </td>
                  <td>
                    {isOwner && u.id !== me.partner_user.id && (
                      <>
                        {u.status === "active" ? (
                          <button className="hw-pp__btn" style={{ padding: "0.2rem 0.45rem" }}
                                   onClick={() => setStatus(u.id, "disabled")}
                                   data-testid={`pp-user-disable-${u.id}`}>Disable</button>
                        ) : u.status === "disabled" ? (
                          <button className="hw-pp__btn" style={{ padding: "0.2rem 0.45rem" }}
                                   onClick={() => setStatus(u.id, "active")}
                                   data-testid={`pp-user-enable-${u.id}`}>Enable</button>
                        ) : null}
                        <button className="hw-pp__btn hw-pp__btn--danger"
                                 style={{ padding: "0.2rem 0.45rem", marginLeft: "0.3rem" }}
                                 onClick={() => remove(u.id, u.email)}
                                 data-testid={`pp-user-remove-${u.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
